import { useState, useEffect } from "react";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import {
  collection, getDocs, doc, updateDoc, query, where,
  setDoc, serverTimestamp, addDoc, Timestamp, getDoc,
  arrayUnion, arrayRemove
} from "firebase/firestore";

const ADMIN_EMAIL = "jonnybroughton@me.com";

const ALL_TEAMS = [
  "Algeria", "Argentina", "Australia", "Austria",
  "Belgium", "Bosnia & Herzegovina", "Brazil",
  "Canada", "Cape Verde", "Colombia", "Croatia", "Czech Republic", "Curaçao",
  "DR Congo",
  "Ecuador", "Egypt", "England",
  "France",
  "Germany", "Ghana",
  "Haiti",
  "Iran", "Iraq", "Ivory Coast",
  "Japan", "Jordan",
  "Mexico", "Morocco",
  "Netherlands", "New Zealand", "Norway",
  "Panama", "Paraguay", "Portugal",
  "Qatar",
  "Saudi Arabia", "Scotland", "Senegal", "South Africa", "South Korea", "Spain", "Sweden", "Switzerland",
  "Tunisia", "Turkey",
  "Uruguay", "USA", "Uzbekistan",
];

export default function Admin() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Match state
  const [matches, setMatches] = useState([]);
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [homeScore, setHomeScore] = useState("");
  const [awayScore, setAwayScore] = useState("");
  const [goalscorer, setGoalscorer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // Seed state
  const [seeding, setSeeding] = useState(false);
  const [seedSuccess, setSeedSuccess] = useState(false);

  // Stats state
  const [userCount, setUserCount] = useState(null);
  const [leagueCount, setLeagueCount] = useState(null);

  // Global league state
  const [creatingGlobal, setCreatingGlobal] = useState(false);
  const [globalLeagueSuccess, setGlobalLeagueSuccess] = useState(false);

  // Premium codes state
  const [codes, setCodes] = useState([]);
  const [newCode, setNewCode] = useState("");
  const [newCodeExpiry, setNewCodeExpiry] = useState("");
  const [newCodeMaxUses, setNewCodeMaxUses] = useState("");
  const [creatingCode, setCreatingCode] = useState(false);
  const [codeSuccess, setCodeSuccess] = useState("");
  const [codeError, setCodeError] = useState("");

  // Squad state
  const [squads, setSquads] = useState({});
  const [selectedTeamForSquad, setSelectedTeamForSquad] = useState("");
  const [newPlayerName, setNewPlayerName] = useState("");
  const [savingPlayer, setSavingPlayer] = useState(false);
  const [playerSuccess, setPlayerSuccess] = useState("");
  const [playerError, setPlayerError] = useState("");
  const [squadSearch, setSquadSearch] = useState("");

  // Users state
  const [users, setUsers] = useState([]);
  const [showUsers, setShowUsers] = useState(false);
  const [userSearch, setUserSearch] = useState("");

  // Private leagues state
  const [privateLeagues, setPrivateLeagues] = useState([]);
  const [showLeagues, setShowLeagues] = useState(false);
  const [leagueSearch, setLeagueSearch] = useState("");

  const [bulkPlayers, setBulkPlayers] = useState("");
const [bulkImporting, setBulkImporting] = useState(false);
const [bulkSuccess, setBulkSuccess] = useState("");

  useEffect(() => {
    if (currentUser.email !== ADMIN_EMAIL) navigate("/");
    fetchMatches();
    fetchStats();
    fetchCodes();
    fetchSquads();
    fetchUsers();
    fetchPrivateLeagues();
  }, []);

  // ---- Fetch functions ----

  async function fetchMatches() {
    const snapshot = await getDocs(collection(db, "matches"));
    const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    data.sort((a, b) => new Date(a.kickoff) - new Date(b.kickoff));
    setMatches(data);
  }

  async function fetchStats() {
    const usersSnap = await getDocs(collection(db, "users"));
    setUserCount(usersSnap.size);
    const leaguesSnap = await getDocs(collection(db, "leagues"));
    setLeagueCount(leaguesSnap.size);
  }

  async function fetchCodes() {
    const snap = await getDocs(collection(db, "premiumCodes"));
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    data.sort((a, b) => b.createdAt?.toMillis() - a.createdAt?.toMillis());
    setCodes(data);
  }

  async function fetchSquads() {
    const snap = await getDocs(collection(db, "squads"));
    const data = {};
    snap.docs.forEach((d) => {
      data[d.id] = d.data().players || [];
    });
    setSquads(data);
  }

  async function fetchUsers() {
    const snap = await getDocs(collection(db, "users"));
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    data.sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));
    setUsers(data);
  }

  async function fetchPrivateLeagues() {
    const snap = await getDocs(collection(db, "leagues"));
    const data = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((l) => !l.isPublic);
    data.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    setPrivateLeagues(data);
  }

  // ---- Match result handlers ----

  function getWinner(home, away, match) {
    if (home > away) return match.homeTeam;
    if (away > home) return match.awayTeam;
    return "Draw";
  }

  function handleSelectMatch(match) {
    if (selectedMatchId === match.id) {
      setSelectedMatchId(null);
    } else {
      setSelectedMatchId(match.id);
      setHomeScore("");
      setAwayScore("");
      setGoalscorer("");
      setError("");
      setSuccess("");
    }
  }

  async function handleSubmitResult(e, match) {
    e.preventDefault();
    if (homeScore === "" || awayScore === "") return setError("Please enter a score.");
    setSubmitting(true);
    setError("");
    setSuccess("");

    const home = Number(homeScore);
    const away = Number(awayScore);
    const actualWinner = getWinner(home, away, match);

    try {
      await updateDoc(doc(db, "matches", match.id), {
        result: `${home}-${away}`,
        firstGoalScorer: goalscorer,
        status: "completed",
      });

      const predsSnap = await getDocs(
        query(collection(db, "predictions"), where("matchId", "==", match.id))
      );

      const updates = predsSnap.docs.map(async (predDoc) => {
        const pred = predDoc.data();
        let points = 0;
        const predictedGoalscorer = (pred.goalscorer || "").toLowerCase().trim();
        const actualGoalscorer = goalscorer.toLowerCase().trim();

        if (pred.homeScore === home && pred.awayScore === away) {
          points += 5;
        } else if (pred.winner === actualWinner) {
          points += 2;
        }

        if (predictedGoalscorer && actualGoalscorer && predictedGoalscorer === actualGoalscorer) {
          points += 3;
        }

        await updateDoc(doc(db, "predictions", predDoc.id), { pointsEarned: points });
      });

      await Promise.all(updates);
      setSuccess(`Result saved! Points calculated for ${predsSnap.docs.length} prediction(s).`);
      setSelectedMatchId(null);
      setHomeScore("");
      setAwayScore("");
      setGoalscorer("");
      fetchMatches();
    } catch (err) {
      setError("Something went wrong: " + err.message);
    }
    setSubmitting(false);
  }

  // ---- Premium code handlers ----

  async function handleCreateCode(e) {
    e.preventDefault();
    if (!newCode.trim()) return;
    setCreatingCode(true);
    setCodeError("");
    setCodeSuccess("");
    try {
      const existing = await getDocs(
        query(collection(db, "premiumCodes"), where("code", "==", newCode.trim().toUpperCase()))
      );
      if (!existing.empty) {
        setCodeError("That code already exists.");
        setCreatingCode(false);
        return;
      }
      await addDoc(collection(db, "premiumCodes"), {
        code: newCode.trim().toUpperCase(),
        isActive: true,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        expiresAt: newCodeExpiry ? Timestamp.fromDate(new Date(newCodeExpiry)) : null,
        maxUses: newCodeMaxUses ? Number(newCodeMaxUses) : null,
        usedBy: [],
      });
      setNewCode("");
      setNewCodeExpiry("");
      setNewCodeMaxUses("");
      setCodeSuccess("Code created successfully!");
      fetchCodes();
    } catch (err) {
      setCodeError("Failed to create code: " + err.message);
    }
    setCreatingCode(false);
  }

  async function handleExpireCode(codeId) {
    await updateDoc(doc(db, "premiumCodes", codeId), { isActive: false });
    fetchCodes();
  }

  async function handleReactivateCode(codeId) {
    await updateDoc(doc(db, "premiumCodes", codeId), { isActive: true });
    fetchCodes();
  }

  // ---- Squad handlers ----

  async function handleAddPlayer(e) {
    e.preventDefault();
    if (!selectedTeamForSquad || !newPlayerName.trim()) return;
    setSavingPlayer(true);
    setPlayerError("");
    setPlayerSuccess("");
    try {
      const ref = doc(db, "squads", selectedTeamForSquad);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        await updateDoc(ref, { players: arrayUnion(newPlayerName.trim()) });
      } else {
        await setDoc(ref, { team: selectedTeamForSquad, players: [newPlayerName.trim()] });
      }
      setPlayerSuccess(`${newPlayerName.trim()} added to ${selectedTeamForSquad}`);
      setNewPlayerName("");
      fetchSquads();
    } catch (err) {
      setPlayerError("Failed to add player: " + err.message);
    }
    setSavingPlayer(false);
  }

  async function handleRemovePlayer(team, player) {
    await updateDoc(doc(db, "squads", team), {
      players: arrayRemove(player),
    });
    fetchSquads();
  }

  async function handleRemovePlayer(team, player) {
  await updateDoc(doc(db, "squads", team), {
    players: arrayRemove(player),
  });
  fetchSquads();
}

async function handleBulkImport() {
  if (!selectedTeamForSquad || !bulkPlayers.trim()) return;
  setBulkImporting(true);
  setBulkSuccess("");
  setPlayerError("");
  try {
    const names = bulkPlayers
      .split("\n")
      .map((n) => n.trim())
      .filter((n) => n.length > 0);

    const ref = doc(db, "squads", selectedTeamForSquad);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      await updateDoc(ref, { players: arrayUnion(...names) });
    } else {
      await setDoc(ref, { team: selectedTeamForSquad, players: names });
    }
    setBulkSuccess(`${names.length} players imported to ${selectedTeamForSquad}`);
    setBulkPlayers("");
    fetchSquads();
  } catch (err) {
    setPlayerError("Bulk import failed: " + err.message);
  }
  setBulkImporting(false);
}

  // ---- Global league ----

  async function handleCreateGlobalLeague() {
    if (!window.confirm("Create the global public league? Only do this once.")) return;
    setCreatingGlobal(true);
    try {
      await setDoc(doc(db, "leagues", "global_predictorz"), {
        name: "Predictorz Global League",
        isPublic: true,
        createdBy: currentUser.uid,
        inviteCode: "GLOBAL",
        members: [],
        createdAt: serverTimestamp(),
      });
      setGlobalLeagueSuccess(true);
    } catch (err) {
      setError("Failed to create global league: " + err.message);
    }
    setCreatingGlobal(false);
  }

  // ---- Fixtures data ----

  const wc2026Fixtures = [
    { homeTeam: "Mexico", awayTeam: "South Africa", kickoff: "2026-06-11T20:00:00+01:00", group: "A", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "South Korea", awayTeam: "Czech Republic", kickoff: "2026-06-12T03:00:00+01:00", group: "A", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Czech Republic", awayTeam: "South Africa", kickoff: "2026-06-18T17:00:00+01:00", group: "A", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Mexico", awayTeam: "South Korea", kickoff: "2026-06-19T02:00:00+01:00", group: "A", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Mexico", awayTeam: "Czech Republic", kickoff: "2026-06-24T20:00:00+01:00", group: "A", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "South Africa", awayTeam: "South Korea", kickoff: "2026-06-24T20:00:00+01:00", group: "A", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Canada", awayTeam: "Bosnia & Herzegovina", kickoff: "2026-06-12T20:00:00+01:00", group: "B", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Qatar", awayTeam: "Switzerland", kickoff: "2026-06-12T23:00:00+01:00", group: "B", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Switzerland", awayTeam: "Bosnia & Herzegovina", kickoff: "2026-06-18T20:00:00+01:00", group: "B", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Canada", awayTeam: "Qatar", kickoff: "2026-06-18T23:00:00+01:00", group: "B", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Qatar", awayTeam: "Bosnia & Herzegovina", kickoff: "2026-06-25T20:00:00+01:00", group: "B", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Switzerland", awayTeam: "Canada", kickoff: "2026-06-25T20:00:00+01:00", group: "B", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Brazil", awayTeam: "Morocco", kickoff: "2026-06-12T23:00:00+01:00", group: "C", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Scotland", awayTeam: "Haiti", kickoff: "2026-06-13T02:00:00+01:00", group: "C", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Scotland", awayTeam: "Morocco", kickoff: "2026-06-19T23:00:00+01:00", group: "C", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Brazil", awayTeam: "Haiti", kickoff: "2026-06-20T02:00:00+01:00", group: "C", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Morocco", awayTeam: "Haiti", kickoff: "2026-06-26T20:00:00+01:00", group: "C", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Brazil", awayTeam: "Scotland", kickoff: "2026-06-26T23:00:00+01:00", group: "C", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "USA", awayTeam: "Paraguay", kickoff: "2026-06-13T02:00:00+01:00", group: "D", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Australia", awayTeam: "Turkey", kickoff: "2026-06-13T05:00:00+01:00", group: "D", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "USA", awayTeam: "Australia", kickoff: "2026-06-19T20:00:00+01:00", group: "D", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Turkey", awayTeam: "Paraguay", kickoff: "2026-06-20T05:00:00+01:00", group: "D", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "USA", awayTeam: "Turkey", kickoff: "2026-06-25T23:00:00+01:00", group: "D", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Paraguay", awayTeam: "Australia", kickoff: "2026-06-25T23:00:00+01:00", group: "D", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Germany", awayTeam: "Ivory Coast", kickoff: "2026-06-13T21:00:00+01:00", group: "E", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Ecuador", awayTeam: "Curaçao", kickoff: "2026-06-14T01:00:00+01:00", group: "E", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Germany", awayTeam: "Ecuador", kickoff: "2026-06-20T21:00:00+01:00", group: "E", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Ivory Coast", awayTeam: "Curaçao", kickoff: "2026-06-21T00:00:00+01:00", group: "E", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Germany", awayTeam: "Curaçao", kickoff: "2026-06-26T17:00:00+01:00", group: "E", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Ecuador", awayTeam: "Ivory Coast", kickoff: "2026-06-26T17:00:00+01:00", group: "E", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Sweden", awayTeam: "Tunisia", kickoff: "2026-06-13T03:00:00+01:00", group: "F", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Netherlands", awayTeam: "Japan", kickoff: "2026-06-14T21:00:00+01:00", group: "F", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Netherlands", awayTeam: "Sweden", kickoff: "2026-06-20T18:00:00+01:00", group: "F", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Tunisia", awayTeam: "Japan", kickoff: "2026-06-21T05:00:00+01:00", group: "F", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Netherlands", awayTeam: "Tunisia", kickoff: "2026-06-26T20:00:00+01:00", group: "F", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Japan", awayTeam: "Sweden", kickoff: "2026-06-26T20:00:00+01:00", group: "F", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Belgium", awayTeam: "Egypt", kickoff: "2026-06-14T20:00:00+01:00", group: "G", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Iran", awayTeam: "New Zealand", kickoff: "2026-06-15T02:00:00+01:00", group: "G", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Belgium", awayTeam: "Iran", kickoff: "2026-06-20T20:00:00+01:00", group: "G", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Egypt", awayTeam: "New Zealand", kickoff: "2026-06-21T20:00:00+01:00", group: "G", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Belgium", awayTeam: "New Zealand", kickoff: "2026-06-27T23:00:00+01:00", group: "G", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Egypt", awayTeam: "Iran", kickoff: "2026-06-27T23:00:00+01:00", group: "G", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Spain", awayTeam: "Cape Verde", kickoff: "2026-06-14T17:00:00+01:00", group: "H", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Saudi Arabia", awayTeam: "Uruguay", kickoff: "2026-06-14T23:00:00+01:00", group: "H", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Spain", awayTeam: "Saudi Arabia", kickoff: "2026-06-20T20:00:00+01:00", group: "H", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Cape Verde", awayTeam: "Uruguay", kickoff: "2026-06-21T21:00:00+01:00", group: "H", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Spain", awayTeam: "Uruguay", kickoff: "2026-06-27T20:00:00+01:00", group: "H", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Saudi Arabia", awayTeam: "Cape Verde", kickoff: "2026-06-27T20:00:00+01:00", group: "H", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "France", awayTeam: "Senegal", kickoff: "2026-06-15T20:00:00+01:00", group: "I", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Iraq", awayTeam: "Norway", kickoff: "2026-06-15T23:00:00+01:00", group: "I", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "France", awayTeam: "Iraq", kickoff: "2026-06-21T20:00:00+01:00", group: "I", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Senegal", awayTeam: "Norway", kickoff: "2026-06-21T23:00:00+01:00", group: "I", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "France", awayTeam: "Norway", kickoff: "2026-06-27T17:00:00+01:00", group: "I", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Iraq", awayTeam: "Senegal", kickoff: "2026-06-27T17:00:00+01:00", group: "I", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Argentina", awayTeam: "Algeria", kickoff: "2026-06-16T02:00:00+01:00", group: "J", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Austria", awayTeam: "Jordan", kickoff: "2026-06-16T05:00:00+01:00", group: "J", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Argentina", awayTeam: "Austria", kickoff: "2026-06-22T02:00:00+01:00", group: "J", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Algeria", awayTeam: "Jordan", kickoff: "2026-06-22T05:00:00+01:00", group: "J", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Argentina", awayTeam: "Jordan", kickoff: "2026-06-27T21:00:00+01:00", group: "J", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Algeria", awayTeam: "Austria", kickoff: "2026-06-27T21:00:00+01:00", group: "J", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Portugal", awayTeam: "DR Congo", kickoff: "2026-06-15T18:00:00+01:00", group: "K", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Colombia", awayTeam: "Uzbekistan", kickoff: "2026-06-16T00:00:00+01:00", group: "K", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Portugal", awayTeam: "Colombia", kickoff: "2026-06-22T20:00:00+01:00", group: "K", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "DR Congo", awayTeam: "Uzbekistan", kickoff: "2026-06-22T23:00:00+01:00", group: "K", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Portugal", awayTeam: "Uzbekistan", kickoff: "2026-06-27T02:00:00+01:00", group: "K", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "DR Congo", awayTeam: "Colombia", kickoff: "2026-06-27T02:00:00+01:00", group: "K", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "England", awayTeam: "Croatia", kickoff: "2026-06-17T21:00:00+01:00", group: "L", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Ghana", awayTeam: "Panama", kickoff: "2026-06-17T21:00:00+01:00", group: "L", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "England", awayTeam: "Ghana", kickoff: "2026-06-23T21:00:00+01:00", group: "L", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Croatia", awayTeam: "Panama", kickoff: "2026-06-23T21:00:00+01:00", group: "L", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "England", awayTeam: "Panama", kickoff: "2026-06-27T22:00:00+01:00", group: "L", status: "upcoming", result: "", firstGoalScorer: "" },
    { homeTeam: "Croatia", awayTeam: "Ghana", kickoff: "2026-06-27T22:00:00+01:00", group: "L", status: "upcoming", result: "", firstGoalScorer: "" },
  ];

  async function handleSeedFixtures() {
    if (!window.confirm("This will add all 72 group stage fixtures to Firestore. Are you sure?")) return;
    setSeeding(true);
    try {
      await Promise.all(
        wc2026Fixtures.map((fixture, index) =>
          setDoc(doc(db, "matches", `wc2026_${String(index + 1).padStart(3, "0")}`), {
            ...fixture,
            tournament: "FIFA World Cup 2026",
          })
        )
      );
      setSeedSuccess(true);
      fetchMatches();
    } catch (err) {
      setError("Seeding failed: " + err.message);
    }
    setSeeding(false);
  }

  // ---- Helpers ----

  function getMatchPlayers(match) {
    const home = (squads[match.homeTeam] || []).map((p) => ({ player: p, team: match.homeTeam }));
    const away = (squads[match.awayTeam] || []).map((p) => ({ player: p, team: match.awayTeam }));
    return [...home, ...away].sort((a, b) => a.player.localeCompare(b.player));
  }

  const hasSquadData = (match) =>
    (squads[match.homeTeam] || []).length > 0 || (squads[match.awayTeam] || []).length > 0;

  const filteredUsers = users.filter((u) => {
    const q = userSearch.toLowerCase();
    return (
      (u.displayName || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.firstName || "").toLowerCase().includes(q) ||
      (u.lastName || "").toLowerCase().includes(q)
    );
  });

  const filteredLeagues = privateLeagues.filter((l) =>
    (l.name || "").toLowerCase().includes(leagueSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F0F1EC" }}>
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-10">

        {/* Header */}
        <div className="mb-10 fade-up">
          <p className="font-condensed text-gray-500 tracking-widest uppercase text-sm mb-2">Admin Only</p>
          <h2 className="font-display text-7xl tracking-wider" style={{ color: "#0A0B0F" }}>ADMIN PANEL</h2>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8 fade-up fade-up-delay-1">
          <div className="card p-6" style={{ borderTop: "3px solid #C8FF00" }}>
            <div className="font-display text-6xl mb-1" style={{ color: "#0A0B0F" }}>
              {userCount !== null ? userCount : "—"}
            </div>
            <div className="font-condensed text-gray-400 tracking-widest uppercase text-sm">Users Signed Up</div>
          </div>
          <div className="card p-6" style={{ borderTop: "3px solid #FF5C00" }}>
            <div className="font-display text-6xl mb-1" style={{ color: "#0A0B0F" }}>
              {leagueCount !== null ? leagueCount : "—"}
            </div>
            <div className="font-condensed text-gray-400 tracking-widest uppercase text-sm">Leagues Created</div>
          </div>
        </div>

        {/* Users List */}
        <div className="card overflow-hidden mb-8 fade-up">
          <button
            onClick={() => setShowUsers(!showUsers)}
            className="w-full flex justify-between items-center px-6 py-5 text-left transition-all"
            style={{ borderBottom: showUsers ? "1px solid #E0E2DB" : "none" }}
          >
            <div>
              <h3 className="font-display text-2xl tracking-wider" style={{ color: "#0A0B0F" }}>
                REGISTERED USERS
              </h3>
              <p className="font-condensed text-gray-400 tracking-wide text-sm mt-1">
                {users.length} total users signed up
              </p>
            </div>
            <span className="font-condensed text-gray-400 text-lg">{showUsers ? "▲" : "▼"}</span>
          </button>

          {showUsers && (
            <div>
              <div className="px-6 py-4" style={{ borderBottom: "1px solid #E0E2DB", backgroundColor: "#F8FAF5" }}>
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="w-full rounded-xl px-4 py-3 text-base"
                />
              </div>
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {filteredUsers.map((user, index) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between px-6 py-4"
                    style={{ borderBottom: "1px solid #E0E2DB" }}
                  >
                    <div className="flex items-center gap-4">
                      <span className="font-condensed text-gray-300 text-sm w-8">{index + 1}</span>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-condensed text-lg tracking-wide" style={{ color: "#0A0B0F" }}>
                            {user.displayName || "No display name"}
                          </span>
                          {user.isPremium && (
                            <span className="font-condensed text-xs px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: "#EDFFD0", color: "#3A6600" }}>
                              PREMIUM
                            </span>
                          )}
                        </div>
                        <div className="font-condensed text-sm text-gray-400 tracking-wide">
                          {user.firstName} {user.lastName}
                          {user.firstName || user.lastName ? " · " : ""}
                          {user.email}
                        </div>
                      </div>
                    </div>
                    <div className="font-condensed text-xs text-gray-400 tracking-wide text-right">
                      {user.createdAt?.toDate
                        ? user.createdAt.toDate().toLocaleDateString("en-GB", {
                            day: "numeric", month: "short", year: "numeric"
                          })
                        : "—"}
                    </div>
                  </div>
                ))}
                {filteredUsers.length === 0 && (
                  <div className="px-6 py-8 text-center font-condensed text-gray-400 tracking-wide">
                    No users found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Private Leagues List */}
        <div className="card overflow-hidden mb-8 fade-up">
          <button
            onClick={() => setShowLeagues(!showLeagues)}
            className="w-full flex justify-between items-center px-6 py-5 text-left transition-all"
            style={{ borderBottom: showLeagues ? "1px solid #E0E2DB" : "none" }}
          >
            <div>
              <h3 className="font-display text-2xl tracking-wider" style={{ color: "#0A0B0F" }}>
                PRIVATE LEAGUES
              </h3>
              <p className="font-condensed text-gray-400 tracking-wide text-sm mt-1">
                {privateLeagues.length} private league{privateLeagues.length !== 1 ? "s" : ""} created
              </p>
            </div>
            <span className="font-condensed text-gray-400 text-lg">{showLeagues ? "▲" : "▼"}</span>
          </button>

          {showLeagues && (
            <div>
              <div className="px-6 py-4" style={{ borderBottom: "1px solid #E0E2DB", backgroundColor: "#F8FAF5" }}>
                <input
                  type="text"
                  placeholder="Search leagues..."
                  value={leagueSearch}
                  onChange={(e) => setLeagueSearch(e.target.value)}
                  className="w-full rounded-xl px-4 py-3 text-base"
                />
              </div>
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {filteredLeagues.map((league) => (
                  <div
                    key={league.id}
                    className="flex items-center justify-between px-6 py-4"
                    style={{ borderBottom: "1px solid #E0E2DB" }}
                  >
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-display text-xl tracking-wider" style={{ color: "#0A0B0F" }}>
                          {league.name}
                        </span>
                        <span
                          className="font-condensed text-xs tracking-widest px-2 py-0.5 rounded-lg"
                          style={{ backgroundColor: "#F5F6F2", color: "#666", fontFamily: "monospace" }}
                        >
                          {league.inviteCode}
                        </span>
                      </div>
                      <div className="font-condensed text-sm text-gray-400 tracking-wide">
                        {league.members?.length || 0} member{(league.members?.length || 0) !== 1 ? "s" : ""}
                        {league.pendingMembers?.length > 0 && (
                          <span className="ml-2" style={{ color: "#B85C00" }}>
                            · {league.pendingMembers.length} pending
                          </span>
                        )}
                      </div>
                    </div>
                    <a
                      href={`/league/${league.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-condensed tracking-wide text-sm px-4 py-2 rounded-lg transition-all"
                      style={{ backgroundColor: "#F0F1EC", color: "#0A0B0F", border: "1px solid #E0E2DB" }}
                    >
                      VIEW
                    </a>
                  </div>
                ))}
                {filteredLeagues.length === 0 && (
                  <div className="px-6 py-8 text-center font-condensed text-gray-400 tracking-wide">
                    No leagues found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Global League */}
        <div className="card p-6 mb-8 fade-up" style={{ borderLeft: "4px solid #3B82F6" }}>
          <h3 className="font-display text-2xl tracking-wider mb-1" style={{ color: "#0A0B0F" }}>
            GLOBAL PUBLIC LEAGUE
          </h3>
          <p className="font-condensed text-gray-400 tracking-wide text-sm mb-4">
            Creates the global league visible to all signed-up users. Only needs to be done once.
          </p>
          {globalLeagueSuccess && (
            <p className="font-condensed mb-3 tracking-wide" style={{ color: "#3A6600" }}>
              ✓ Global league created!
            </p>
          )}
          <button
            onClick={handleCreateGlobalLeague}
            disabled={creatingGlobal}
            className="font-condensed font-bold text-lg tracking-wide px-8 py-3 rounded-xl transition-all"
            style={{ backgroundColor: "#3B82F6", color: "#fff" }}
          >
            {creatingGlobal ? "CREATING..." : "CREATE GLOBAL LEAGUE"}
          </button>
        </div>

        {/* Premium Codes */}
        <div className="card overflow-hidden mb-8 fade-up">
          <div className="px-6 py-5" style={{ borderBottom: "1px solid #E0E2DB" }}>
            <h3 className="font-display text-2xl tracking-wider" style={{ color: "#0A0B0F" }}>
              PREMIUM CODES
            </h3>
            <p className="font-condensed text-gray-400 tracking-wide text-sm mt-1">
              Create codes to grant premium access without payment
            </p>
          </div>
          <div className="px-6 py-6" style={{ borderBottom: "2px solid #E0E2DB", backgroundColor: "#F8FAF5" }}>
            <h4 className="font-condensed text-gray-500 tracking-widest uppercase text-sm mb-4">
              Create New Code
            </h4>
            {codeSuccess && (
              <div className="mb-4 px-4 py-3 rounded-xl font-condensed tracking-wide"
                style={{ backgroundColor: "#EDFFD0", border: "1px solid #C8FF00", color: "#3A6600" }}>
                ✓ {codeSuccess}
              </div>
            )}
            {codeError && (
              <div className="mb-4 px-4 py-3 rounded-xl font-condensed tracking-wide text-red-600"
                style={{ backgroundColor: "#FFF0F0", border: "1px solid #FFCCCC" }}>
                {codeError}
              </div>
            )}
            <form onSubmit={handleCreateCode} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="font-condensed text-gray-400 tracking-widest text-xs uppercase block mb-2">Code</label>
                  <input
                    type="text"
                    placeholder="e.g. EARLYBIRD"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                    className="w-full rounded-xl px-4 py-3 text-lg"
                    style={{ fontFamily: "monospace", letterSpacing: "0.1em" }}
                  />
                </div>
                <div>
                  <label className="font-condensed text-gray-400 tracking-widest text-xs uppercase block mb-2">
                    Expiry Date <span className="normal-case text-gray-300">(optional)</span>
                  </label>
                  <input
                    type="date"
                    value={newCodeExpiry}
                    onChange={(e) => setNewCodeExpiry(e.target.value)}
                    className="w-full rounded-xl px-4 py-3 text-lg"
                  />
                </div>
                <div>
                  <label className="font-condensed text-gray-400 tracking-widest text-xs uppercase block mb-2">
                    Max Uses <span className="normal-case text-gray-300">(optional)</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Unlimited"
                    value={newCodeMaxUses}
                    onChange={(e) => setNewCodeMaxUses(e.target.value)}
                    className="w-full rounded-xl px-4 py-3 text-lg"
                  />
                </div>
              </div>
              <button type="submit" disabled={creatingCode} className="btn-lime px-8 py-3 rounded-xl">
                {creatingCode ? "CREATING..." : "CREATE CODE"}
              </button>
            </form>
          </div>
          {codes.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <p className="font-condensed text-gray-400 tracking-wide">No codes created yet</p>
            </div>
          ) : (
            codes.map((code) => {
              const isExpiredByDate = code.expiresAt && code.expiresAt.toDate() < new Date();
              const isMaxedOut = code.maxUses && code.usedBy.length >= code.maxUses;
              const effectivelyActive = code.isActive && !isExpiredByDate && !isMaxedOut;
              return (
                <div key={code.id} className="flex items-center justify-between px-6 py-4"
                  style={{ borderBottom: "1px solid #E0E2DB" }}>
                  <div className="flex items-center gap-4 flex-1">
                    <div style={{ minWidth: "140px" }}>
                      <span className="font-condensed text-xl tracking-widest"
                        style={{ color: "#0A0B0F", fontFamily: "monospace" }}>
                        {code.code}
                      </span>
                    </div>
                    <span className="font-condensed text-xs tracking-wider px-3 py-1 rounded-full"
                      style={effectivelyActive
                        ? { backgroundColor: "#EDFFD0", color: "#3A6600" }
                        : { backgroundColor: "#FFF0F0", color: "#CC0000" }
                      }>
                      {!code.isActive ? "EXPIRED" : isExpiredByDate ? "DATE EXPIRED" : isMaxedOut ? "MAXED OUT" : "ACTIVE"}
                    </span>
                    <span className="font-condensed text-gray-400 text-sm tracking-wide">
                      {code.usedBy.length} use{code.usedBy.length !== 1 ? "s" : ""}
                      {code.maxUses ? ` / ${code.maxUses} max` : ""}
                    </span>
                    {code.expiresAt && (
                      <span className="font-condensed text-gray-400 text-sm tracking-wide">
                        Expires {code.expiresAt.toDate().toLocaleDateString("en-GB", {
                          day: "numeric", month: "short", year: "numeric"
                        })}
                      </span>
                    )}
                  </div>
                  <div>
                    {effectivelyActive ? (
                      <button onClick={() => handleExpireCode(code.id)}
                        className="font-condensed tracking-wide text-sm px-4 py-2 rounded-lg transition"
                        style={{ border: "1px solid #FFCCCC", color: "#CC0000", backgroundColor: "#FFF0F0" }}>
                        EXPIRE
                      </button>
                    ) : (
                      !code.isActive && (
                        <button onClick={() => handleReactivateCode(code.id)}
                          className="font-condensed tracking-wide text-sm px-4 py-2 rounded-lg transition"
                          style={{ border: "1px solid #C8FF00", color: "#3A6600", backgroundColor: "#EDFFD0" }}>
                          REACTIVATE
                        </button>
                      )
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

                {/* Squad Management */}
        <div className="card overflow-hidden mb-8 fade-up">
          <div className="px-6 py-5" style={{ borderBottom: "1px solid #E0E2DB" }}>
            <h3 className="font-display text-2xl tracking-wider" style={{ color: "#0A0B0F" }}>
              SQUAD MANAGEMENT
            </h3>
            <p className="font-condensed text-gray-400 tracking-wide text-sm mt-1">
              Add players to each team so they appear in the goalscorer dropdown
            </p>
          </div>

          {/* Team selector */}
          <div className="px-6 py-5" style={{ borderBottom: "1px solid #E0E2DB", backgroundColor: "#F8FAF5" }}>
            <label className="font-condensed text-gray-400 tracking-widest text-xs uppercase block mb-2">
              Select Team
            </label>
            <select
              value={selectedTeamForSquad}
              onChange={(e) => { setSelectedTeamForSquad(e.target.value); setSquadSearch(""); setBulkPlayers(""); setBulkSuccess(""); }}
              className="w-full rounded-xl px-4 py-3 text-lg"
            >
              <option value="">Choose a team...</option>
              {ALL_TEAMS.map((team) => (
                <option key={team} value={team}>{team}</option>
              ))}
            </select>
          </div>

          {/* Add single player */}
          <div className="px-6 py-6" style={{ borderBottom: "1px solid #E0E2DB", backgroundColor: "#F8FAF5" }}>
            <h4 className="font-condensed text-gray-500 tracking-widest uppercase text-sm mb-4">
              Add Single Player
            </h4>
            {playerSuccess && (
              <div className="mb-4 px-4 py-3 rounded-xl font-condensed tracking-wide"
                style={{ backgroundColor: "#EDFFD0", border: "1px solid #C8FF00", color: "#3A6600" }}>
                ✓ {playerSuccess}
              </div>
            )}
            {playerError && (
              <div className="mb-4 px-4 py-3 rounded-xl font-condensed tracking-wide text-red-600"
                style={{ backgroundColor: "#FFF0F0", border: "1px solid #FFCCCC" }}>
                {playerError}
              </div>
            )}
            <form onSubmit={handleAddPlayer} className="flex gap-3">
              <input
                type="text"
                placeholder="e.g. Harry Kane"
                value={newPlayerName}
                onChange={(e) => setNewPlayerName(e.target.value)}
                className="flex-1 rounded-xl px-4 py-3 text-lg"
              />
              <button
                type="submit"
                disabled={savingPlayer || !selectedTeamForSquad || !newPlayerName.trim()}
                className="btn-lime px-6 py-3 rounded-xl"
              >
                {savingPlayer ? "ADDING..." : "ADD"}
              </button>
            </form>
          </div>

          {/* Bulk import */}
           {/* Bulk import */}
          <div className="px-6 py-6" style={{ borderBottom: "2px solid #E0E2DB", backgroundColor: "#FAFFF5" }}>
            <h4 className="font-condensed text-gray-500 tracking-widest uppercase text-sm mb-1">
              Bulk Import
            </h4>
            <p className="font-condensed text-gray-400 text-sm tracking-wide mb-3">
              Paste a list of players — one name per line
            </p>
            <textarea
              value={bulkPlayers}
              onChange={(e) => setBulkPlayers(e.target.value)}
              placeholder={"Harry Kane\nJude Bellingham\nBukayo Saka"}
              className="w-full rounded-xl px-4 py-3 text-base font-condensed"
              style={{ minHeight: "160px", border: "1px solid #E0E2DB", resize: "vertical", backgroundColor: "#FFFFFF" }}
            />
            {bulkSuccess && (
              <p className="font-condensed mt-2 tracking-wide text-sm" style={{ color: "#3A6600" }}>
                ✓ {bulkSuccess}
              </p>
            )}
            <button
              type="button"
              onClick={handleBulkImport}
              disabled={bulkImporting || !selectedTeamForSquad || !bulkPlayers.trim()}
              className="btn-lime px-8 py-3 rounded-xl mt-3"
            >
              {bulkImporting ? "IMPORTING..." : "IMPORT PLAYERS"}
            </button>
          </div>


          {/* Squad viewer */}
          {selectedTeamForSquad && (
            <div className="px-6 py-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-display text-xl tracking-wider" style={{ color: "#0A0B0F" }}>
                  {selectedTeamForSquad.toUpperCase()} SQUAD
                  <span className="font-condensed text-gray-400 text-sm ml-3 normal-case tracking-wide">
                    {(squads[selectedTeamForSquad] || []).length} players
                  </span>
                </h4>
                <input
                  type="text"
                  placeholder="Search players..."
                  value={squadSearch}
                  onChange={(e) => setSquadSearch(e.target.value)}
                  className="rounded-xl px-4 py-2 text-base"
                  style={{ width: "200px" }}
                />
              </div>
              {(squads[selectedTeamForSquad] || []).length === 0 ? (
                <p className="font-condensed text-gray-400 tracking-wide">
                  No players added yet for {selectedTeamForSquad}
                </p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {(squads[selectedTeamForSquad] || [])
                    .filter((p) => p.toLowerCase().includes(squadSearch.toLowerCase()))
                    .sort()
                    .map((player) => (
                      <div key={player}
                        className="flex items-center justify-between px-4 py-2 rounded-lg"
                        style={{ backgroundColor: "#F5F6F2", border: "1px solid #E0E2DB" }}>
                        <span className="font-condensed tracking-wide" style={{ color: "#0A0B0F" }}>
                          {player}
                        </span>
                        <button
                          onClick={() => handleRemovePlayer(selectedTeamForSquad, player)}
                          className="font-condensed text-xs ml-2 transition-colors"
                          style={{ color: "#CC0000" }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>


        {/* Seed Fixtures */}
        <div className="card p-6 mb-8 fade-up" style={{ borderLeft: "4px solid #FF5C00" }}>
          <h3 className="font-display text-2xl tracking-wider mb-1" style={{ color: "#0A0B0F" }}>
            SEED WC2026 FIXTURES
          </h3>
          <p className="font-condensed text-gray-400 tracking-wide text-sm mb-4">
            One-time action — populates all 72 group stage matches into Firestore
          </p>
          {seedSuccess && (
            <p className="font-condensed mb-3 tracking-wide" style={{ color: "#3A6600" }}>
              ✓ All 72 fixtures added successfully!
            </p>
          )}
          <button onClick={handleSeedFixtures} disabled={seeding} className="btn-orange px-8 py-3 rounded-xl">
            {seeding ? "SEEDING..." : "SEED ALL WC2026 FIXTURES"}
          </button>
        </div>

        {/* Alerts */}
        {success && (
          <div className="mb-6 px-4 py-3 rounded-xl font-condensed tracking-wide"
            style={{ backgroundColor: "#EDFFD0", border: "1px solid #C8FF00", color: "#3A6600" }}>
            ✓ {success}
          </div>
        )}
        {error && (
          <div className="mb-6 px-4 py-3 rounded-xl font-condensed tracking-wide text-red-600"
            style={{ backgroundColor: "#FFF0F0", border: "1px solid #FFCCCC" }}>
            {error}
          </div>
        )}

        {/* Match list with inline result entry */}
        <div className="card overflow-hidden fade-up fade-up-delay-2">
          <div className="px-6 py-5" style={{ borderBottom: "1px solid #E0E2DB" }}>
            <h3 className="font-display text-2xl tracking-wider" style={{ color: "#0A0B0F" }}>
              ENTER RESULTS
            </h3>
            <p className="font-condensed text-gray-400 tracking-wide text-sm mt-1">
              Click a match to enter its result
            </p>
          </div>

          {matches.map((match) => {
            const isSelected = selectedMatchId === match.id;
            const kickoffDate = new Date(match.kickoff).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
            const kickoffTime = new Date(match.kickoff).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
            const matchPlayers = getMatchPlayers(match);
            const squadLoaded = hasSquadData(match);

            return (
              <div key={match.id} style={{ borderBottom: "1px solid #E0E2DB" }}>
                <button
                  onClick={() => handleSelectMatch(match)}
                  className="w-full flex justify-between items-center px-6 py-4 text-left transition-all"
                  style={{
                    backgroundColor: isSelected ? "#FAFFF5" : "transparent",
                    borderLeft: isSelected ? "4px solid #C8FF00" : "4px solid transparent",
                  }}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="text-right" style={{ minWidth: "80px" }}>
                      <div className="font-condensed text-gray-400 text-xs tracking-widest uppercase">{kickoffDate}</div>
                      <div className="font-condensed text-gray-500 text-sm">{kickoffTime} BST</div>
                    </div>
                    <div className="font-condensed text-lg tracking-wide" style={{ color: "#0A0B0F" }}>
                      <span className="font-condensed text-xs text-gray-400 tracking-widest uppercase mr-2">
                        Grp {match.group}
                      </span>
                      {match.homeTeam} vs {match.awayTeam}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-condensed text-xs tracking-wider px-3 py-1 rounded-full"
                      style={match.status === "completed"
                        ? { backgroundColor: "#EDFFD0", color: "#3A6600" }
                        : { backgroundColor: "#FFF3E0", color: "#B85C00" }
                      }>
                      {match.status === "completed" ? `${match.result} ✓` : "UPCOMING"}
                    </span>
                    <span className="font-condensed text-gray-400 text-lg">
                      {isSelected ? "▲" : "▼"}
                    </span>
                  </div>
                </button>

                {isSelected && (
                  <div className="px-6 py-6" style={{ backgroundColor: "#F8FAF5", borderTop: "1px solid #E0E2DB" }}>
                    <h4 className="font-display text-xl tracking-wider mb-6" style={{ color: "#0A0B0F" }}>
                      ENTER RESULT: {match.homeTeam} vs {match.awayTeam}
                    </h4>
                    <form onSubmit={(e) => handleSubmitResult(e, match)} className="space-y-5">
                      <div className="flex items-center gap-6">
                        <div className="flex-1 text-center">
                          <p className="font-condensed text-gray-400 tracking-wide text-sm mb-2">{match.homeTeam}</p>
                          <input
                            type="number" min="0"
                            value={homeScore}
                            onChange={(e) => setHomeScore(e.target.value)}
                            className="w-full rounded-xl px-4 py-3 text-center"
                            style={{ fontSize: "2rem", fontFamily: "'Bebas Neue', sans-serif", color: "#0A0B0F" }}
                          />
                        </div>
                        <div className="font-display text-3xl text-gray-300">—</div>
                        <div className="flex-1 text-center">
                          <p className="font-condensed text-gray-400 tracking-wide text-sm mb-2">{match.awayTeam}</p>
                          <input
                            type="number" min="0"
                            value={awayScore}
                            onChange={(e) => setAwayScore(e.target.value)}
                            className="w-full rounded-xl px-4 py-3 text-center"
                            style={{ fontSize: "2rem", fontFamily: "'Bebas Neue', sans-serif", color: "#0A0B0F" }}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="font-condensed text-gray-400 tracking-widest text-xs uppercase block mb-2">
                          First Goalscorer <span className="normal-case text-gray-300">(leave blank for 0-0)</span>
                        </label>
                        {squadLoaded ? (
                          <select
                            value={goalscorer}
                            onChange={(e) => setGoalscorer(e.target.value)}
                            className="w-full rounded-xl px-4 py-3 text-lg"
                          >
                            <option value="">No goalscorer / 0-0 draw</option>
                            {matchPlayers.map(({ player, team }) => (
                              <option key={`${team}-${player}`} value={player}>
                                {player} ({team})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <>
                            <input
                              type="text"
                              placeholder="e.g. Harry Kane"
                              value={goalscorer}
                              onChange={(e) => setGoalscorer(e.target.value)}
                              className="w-full rounded-xl px-4 py-3 text-lg"
                            />
                            <p className="font-condensed text-gray-400 text-xs mt-2 tracking-wide">
                              No squad data for these teams yet — add players in Squad Management above
                            </p>
                          </>
                        )}
                      </div>
                      <div className="flex gap-3">
                        <button
                          type="submit"
                          disabled={submitting}
                          className="btn-lime px-8 py-3 rounded-xl flex-1"
                        >
                          {submitting ? "SAVING..." : "SAVE RESULT & CALCULATE POINTS"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedMatchId(null)}
                          className="font-condensed tracking-wide px-6 py-3 rounded-xl transition"
                          style={{ border: "1px solid #E0E2DB", color: "#888" }}
                        >
                          CANCEL
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
